<script setup lang="ts">
  import { globalUtils } from '@gat-ship/utils'
  import { computed, ref } from 'vue'
  import type { TenantOwnedDatabase, TenantUser } from '@gat-ship/types'
  import { useDatabaseAccessUtils } from '../../composables/useDatabaseAccessUtils'
  import { testIds } from '@gat-ship/types'
  import { useTenantUserDatabaseAccessGrants } from '../providers/TenantUserDbAccessProvider.vue'
  import { useTenantUserApiCalls } from '../../composables/useTenantUserApiCalls'
  import { IconDatabase, IconLoading } from '@gat-ship/icons'
  import { Switch, Button } from '@gat-ship/vue-components'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'
  const props = withDefaults(
    defineProps<{
      tenantUser: TenantUser
      database: TenantOwnedDatabase
      /**
       * `card` — the standalone bordered card (routed database-access page).
       * `row` — a compact list row for the detail-modal section, matching the
       * user-settings "Databases" list.
       */
      variant?: 'card' | 'row'
    }>(),
    { variant: 'card' }
  )

  const {
    databaseAccessGrants,
    removeDatabaseAccessGrant,
    addDatabaseAccessGrant
  } = useTenantUserDatabaseAccessGrants()

  const {
    getDatabaseAccessGrantRequest,
    grantDatabaseAccessRequest,
    revokeDatabaseAccessRequest
  } = useTenantUserApiCalls()

  const { DatabaseAccessUtils } = useDatabaseAccessUtils()

  const isGrantedDatabaseAccess = computed(() =>
    DatabaseAccessUtils.isGrantedDatabaseAccess(
      props.database.id,
      databaseAccessGrants.value
    )
  )

  const databaseGrant = computed(() =>
    DatabaseAccessUtils.findGrantForDatabase(
      databaseAccessGrants.value,
      props.database.id
    )
  )

  const isLoading = ref(false)

  const confirmDialogOpen = ref(false)

  function setIsLoading() {
    isLoading.value = true
    confirmDialogOpen.value = false
  }

  function clearIsLoading() {
    isLoading.value = false
  }

  async function onRevokeDatabaseAccess() {
    if (!databaseGrant.value) {
      throw new Error('Database grant not found.')
    }
    setIsLoading()

    try {
      await revokeDatabaseAccessRequest(
        {
          userId: props.tenantUser.userId,
          grantId: databaseGrant.value.grantId
        },
        globalUtils.functions.uuid()
      )
      removeDatabaseAccessGrant(databaseGrant.value.grantId)
    } catch (error) {
      console.error(error)
    } finally {
      clearIsLoading()
    }
  }

  async function onGrantDatabaseAccess() {
    if (
      !DatabaseAccessUtils.isValidGrantDatabaseAccessRequest({
        databaseId: props.database.id,
        userId: props.tenantUser.userId
      })
    ) {
      throw new Error('Invalid request payload.')
    }
    try {
      setIsLoading()
      confirmDialogOpen.value = false
      const grantResponse = await grantDatabaseAccessRequest(
        { databaseId: props.database.id, userId: props.tenantUser.userId },
        globalUtils.functions.uuid()
      )
      if (grantResponse?.grantId) {
        const grant = await getDatabaseAccessGrantRequest(
          props.tenantUser.userId,
          grantResponse.grantId
        )
        if (grant?.databaseGrant) {
          addDatabaseAccessGrant(grant.databaseGrant)
        }
      }
    } finally {
      clearIsLoading()
    }
  }

  async function onConfirmDialogOk() {
    if (isGrantedDatabaseAccess.value) {
      onRevokeDatabaseAccess()
    } else {
      onGrantDatabaseAccess()
    }
  }

  function showGrantDatabaseAccessModal() {
    confirmDialogOpen.value = true
  }
</script>
<template>
  <div
    v-if="props.variant === 'row'"
    class="flex items-center justify-between gap-4 py-3"
  >
    <div class="flex min-w-0 items-center gap-2">
      <IconDatabase class="text-muted-foreground size-4 shrink-0" />
      <div class="flex min-w-0 flex-col items-start gap-0.5">
        <span class="truncate text-sm font-medium">{{ database.name }}</span>
        <span
          role="description"
          :data-testid="
            testIds.tenantUser.databaseAccessTable.grantOrRevokeButton(
              database.id
            )
          "
          class="text-muted-foreground text-xs"
        >
          {{ isGrantedDatabaseAccess ? 'Access granted' : 'No access' }}
        </span>
      </div>
    </div>
    <button
      type="button"
      class="shrink-0 cursor-pointer"
      @click="showGrantDatabaseAccessModal"
    >
      <IconLoading v-if="isLoading" class="size-4" />
      <Switch
        v-else
        :model-value="isGrantedDatabaseAccess"
        class="pointer-events-none"
      />
    </button>
  </div>
  <label
    v-else
    class="bg-background shadow-xs ring-border relative flex cursor-pointer items-center gap-3.5 rounded-sm p-4 pr-16 ring-1 ring-inset"
    @click="showGrantDatabaseAccessModal"
  >
    <div
      class="bg-background shadow-xs ring-border flex size-10 shrink-0 items-center justify-center rounded-full ring-1 ring-inset"
    >
      <IconLoading v-if="isLoading" class="size-4" />
      <IconDatabase v-else class="lucide-icon size-4 shrink-0" />
    </div>
    <Switch
      :model-value="isGrantedDatabaseAccess"
      class="pointer-events-none absolute right-5 top-5"
    />
    <div class="space-y-1">
      <div class="flex items-center gap-1 text-sm font-medium">
        <span>{{ database.name }}</span>
      </div>
      <div
        role="description"
        :data-testid="
          testIds.tenantUser.databaseAccessTable.grantOrRevokeButton(
            database.id
          )
        "
        class="text-muted-foreground text-xs"
      >
        {{ isGrantedDatabaseAccess ? 'Access granted' : 'No access' }}
      </div>
    </div>
  </label>
  <AlertDialog v-model:open="confirmDialogOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{
            isGrantedDatabaseAccess
              ? 'Remove Database Access'
              : 'Grant Database Access'
          }}
        </AlertDialogTitle>
      </AlertDialogHeader>
      <div class="flex flex-col gap-4 text-sm">
        <p>
          Are you sure you want to
          {{ isGrantedDatabaseAccess ? 'revoke' : 'grant' }}
          database access?
        </p>
        <p class="text-muted-foreground text-xs">
          {{
            isGrantedDatabaseAccess
              ? `This means ${tenantUser.fullName} will no longer have access to ${database.name}.`
              : `This means ${tenantUser.fullName} will have access to ${database.name}.`
          }}
        </p>
      </div>
      <AlertDialogFooter>
        <Button variant="outline" @click="onConfirmDialogOk()">
          Confirm
        </Button>
        <Button variant="outline" @click="confirmDialogOpen = false">
          Cancel
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
